import {
  calculationSelectStyles,
  operatorSelectStyles,
} from 'apps/builder/components/shared'
import Select from 'react-select'
import {
  createOperation,
  deleteOperation,
  updateOperation,
} from 'apps/builder/store/actions/operations'
import {
  calculationFieldsSelector,
  operationsSelector,
  operationTypesSelector,
  tableCalculationFieldsSelector,
  activeFieldInTableSelector,
} from 'apps/builder/store/selectors'

import { useRef, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { OperationIcon } from 'apps/builder/components/settings/modal/fields/calculation/operation-icons'
import { Input } from 'components/Input/Input'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faTrashAlt,
  faChevronDown,
  faPlus,
} from '@fortawesome/pro-solid-svg-icons'

const CalculationSelect = ({ autoFocus, ...props }) => {
  const inputRef = useRef(null)

  useEffect(() => {
    if (inputRef && autoFocus) {
      inputRef.current.focus()
    }
  }, [inputRef, autoFocus])

  return (
    <Select
      styles={calculationSelectStyles}
      isSearchable={false}
      className="calculation--value--dropdown"
      classNamePrefix="react-select"
      blurInputOnSelect
      ref={inputRef}
      autoFocus={autoFocus}
      {...props}
    />
  )
}

const mapCalculationOptions = ({
  sheet = {},
  entries = [],
  isSubSheet = false,
  primarySheet,
}) => {
  const { description, placeholder } = isSubSheet ? primarySheet : sheet
  return {
    label: description || placeholder,
    options: entries.map(({ label, placeholder, guid }) => ({
      label: label ? label : placeholder,
      value: guid,
    })),
  }
}

const CalculationValue = ({
  autoFocus,
  parentEntryGuid,
  value,
  id,
  entry,
  index,
}) => {
  const dispatch = useDispatch()
  const [showConstant, toggleConstant] = useState(value)
  const constantRef = useRef()

  useEffect(() => {
    if (constantRef.current) {
      constantRef.current.focus()
    }
  }, [constantRef])

  const isCalcFieldInATable = useSelector(activeFieldInTableSelector)

  const calculationFields = isCalcFieldInATable
    ? useSelector(tableCalculationFieldsSelector)
    : useSelector(calculationFieldsSelector)

  const defaultOptions = [{ label: 'Add Constant', value: 'Add Constant' }]

  const options = defaultOptions.concat(
    calculationFields.map(mapCalculationOptions)
  )

  const selectedField = calculationFields
    .map(({ entries }) => entries)
    .flat()
    .find(({ guid }) => guid === parentEntryGuid)

  // TODO: make this less complex
  const calculationValue = value
    ? defaultOptions[0]
    : selectedField
    ? {
        label: selectedField.label,
        value: selectedField.guid,
      }
    : ''

  // If there is no operation ID we need to create it
  const update = id ? updateOperation : createOperation

  const onChange = ({ value }) => {
    if (value === 'Add Constant') {
      toggleConstant(true)
    } else {
      dispatch(
        update({
          id,
          index,
          guid: entry,
          value: '',
          parentEntryGuid: value,
        })
      )
    }
  }

  return (
    <div className="calculation-input--wrapper">
      {showConstant ? (
        <div
          className={`preview--field calculation--value--input--wrapper calculation--constant-input`}
        >
          <Input
            valueRef={constantRef}
            value={value}
            update={value =>
              dispatch(update({ value, index, id, guid: entry }))
            }
            placeholder="Enter a number"
            type="number"
            additionalClass={'calculation--value--input'}
            label={false}
          />
          <div className="calculation--constant-input-toggle">
            <FontAwesomeIcon
              icon={faChevronDown}
              onClick={() => {
                toggleConstant(false)
              }}
            />
          </div>
        </div>
      ) : (
        <CalculationSelect
          autoFocus={autoFocus}
          value={calculationValue}
          options={options}
          defaultMenuIsOpen={value}
          placeholder="Value"
          onChange={onChange}
        />
      )}
    </div>
  )
}

const CalculationOperator = ({
  operation_type_id = 1,
  id,
  entry,
  index,
  operation_type,
}) => {
  const dispatch = useDispatch()
  const operationOptions = useSelector(operationTypesSelector)
  const options = operationOptions.map(({ operation_type, id }) => ({
    label: <OperationIcon type={operation_type} />,
    operation_type,
    value: id,
  }))

  const initialOperator = options.find(
    option => option.value === operation_type_id
  )
  const update = id ? updateOperation : createOperation
  const onChange = ({ value, operation_type }) =>
    dispatch(
      update({
        id,
        operation_type,
        index,
        guid: entry,
        operation_type_id: value,
        operation_type,
      })
    )

  return (
    <Select
      styles={operatorSelectStyles}
      isSearchable={false}
      value={initialOperator}
      className="calculation--operator--dropdown"
      classNamePrefix="react-select"
      options={options}
      blurInputOnSelect
      onChange={onChange}
    />
  )
}

const ensureMinimumCalculationOptions = (calculations, defaultOption) => {
  const minimumOptions = 2
  const calculationCount = calculations.length

  if (calculationCount > minimumOptions) {
    return calculations.sort((a, b) => a.index - b.index)
  }

  const neededOptions = minimumOptions - calculationCount
  const newOptions = Array(neededOptions).fill(defaultOption)
  calculations.forEach(calc => {
    newOptions.splice(calc.index, 0, calc)
  })
  return newOptions
}

const CalculationDropdown = ({ guid }) => {
  const dispatch = useDispatch()
  const allOperations = useSelector(operationsSelector)
  const entryCalculations = allOperations.filter(({ entry }) => entry === guid)
  const operationPlaceholder = {
    entry: guid,
  }
  const calculations = ensureMinimumCalculationOptions(
    entryCalculations,
    operationPlaceholder
  )

  const hasPlaceholderOperations = calculations.some(({ id }) => !id)

  return (
    <div className="calculation-values--container">
      {calculations.map((calculation = {}, i) => {
        return (
          <div className="calculation--value" key={`${calculation.id}-${i}`}>
            {i > 0 ? (
              <CalculationOperator {...calculation} index={i} />
            ) : (
              <div className="placeholder"></div>
            )}
            <div className="dropdown--wrapper">
              <CalculationValue {...calculation} index={i} />
              {calculation.id && (
                <button
                  className="delete--value--button"
                  onClick={() => dispatch(deleteOperation(calculation.id))}
                >
                  <FontAwesomeIcon icon={faTrashAlt} />
                </button>
              )}
            </div>
          </div>
        )
      })}

      <button
        className={`foundry--button button-active-alt btn--round--border btn--sm add--value--button ${
          hasPlaceholderOperations ? 'button--disabled-alt' : 'button--outlined'
        }`}
        disabled={hasPlaceholderOperations}
        onClick={() =>
          dispatch(createOperation({ guid, index: calculations.length + 1 }))
        }
      >
        <span className="button--label btn--with--icon">
          <FontAwesomeIcon className="button--icon" icon={faPlus} /> Add Value
        </span>
      </button>
    </div>
  )
}

export default CalculationDropdown
